import { useDeviceData, useWebSocketStatus } from '@invisoutlet/shared/hooks';
import type { Device, DeviceSensorPayload } from '@invisoutlet/shared/types';
import {
  DEFAULT_OCCUPIED_COLOR,
  DEFAULT_VACANT_COLOR,
  formatRelativeTime,
  generateMockSensorData,
} from '@invisoutlet/shared/utils';

function toFahrenheit(celsius: number): string {
  return `${Math.round((celsius * 9) / 5 + 32)}°F`;
}

function toCelsius(celsius: number): string {
  return `${Math.round(celsius)}°C`;
}

function StatusDot({ status }: { status: DeviceSensorPayload['deviceStatus'] }) {
  const config: Record<DeviceSensorPayload['deviceStatus'], { color: string; label: string }> = {
    online: { color: 'bg-success', label: 'Online' },
    offline: { color: 'bg-danger', label: 'Offline' },
    error: { color: 'bg-warning', label: 'Error' },
  };
  const { color, label } = config[status];
  return (
    <span
      role="img"
      className={`h-2 w-2 shrink-0 rounded-full ${color}`}
      aria-label={label}
      title={label}
    />
  );
}

export interface DeviceSensorCardProps {
  device: Device;
  temperatureUnit: '°F' | '°C';
  selfService?: boolean;
  onEdit?: (device: Device) => void;
  occupiedColor?: string;
  vacantColor?: string;
}

export function DeviceSensorCard({
  device,
  temperatureUnit,
  selfService,
  onEdit,
  occupiedColor,
  vacantColor,
}: DeviceSensorCardProps) {
  const liveData = useDeviceData(device.deviceId);
  const { connected } = useWebSocketStatus();
  const initializing = connected && !liveData;
  // Fall back to deterministic mock data when WebSocket is not yet connected
  const data = liveData ?? generateMockSensorData(device.deviceId);
  const isOffline = data.deviceStatus === 'offline';

  const tempDisplay =
    temperatureUnit === '°C' ? toCelsius(data.temperature) : toFahrenheit(data.temperature);

  const occupancyColor = data.occupancy
    ? (occupiedColor ?? DEFAULT_OCCUPIED_COLOR)
    : (vacantColor ?? DEFAULT_VACANT_COLOR);

  return (
    <div
      className={`rounded-xl border p-4 transition-opacity ${isOffline ? 'opacity-60' : ''}`}
      style={{
        backgroundColor: `${occupancyColor}14`,
        borderColor: `${occupancyColor}59`,
      }}
    >
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-text-primary">{device.deviceName}</p>
          <p className="text-xs text-text-secondary">{device.room || 'Unassigned'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {selfService && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(device)}
              className="rounded px-1.5 py-0.5 text-xs text-text-secondary ring-1 ring-border hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Edit
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <StatusDot status={data.deviceStatus} />
            {isOffline && <span className="text-xs text-text-secondary">Offline</span>}
          </div>
        </div>
      </div>

      {/* Sensor readings */}
      {initializing ? (
        <div
          role="status"
          className="space-y-2"
          aria-busy="true"
          aria-label="Initializing sensor data"
        >
          <div className="h-3 w-3/4 animate-pulse rounded bg-bg-elevated" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-bg-elevated" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-bg-elevated" />
          <p className="pt-1 text-xs text-text-secondary">Initializing…</p>
        </div>
      ) : !isOffline ? (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <div className="flex items-center gap-1.5">
              <span aria-hidden="true">🌡</span>
              <span className="font-medium text-text-primary">{tempDisplay}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span aria-hidden="true">💧</span>
              <span className="font-medium text-text-primary">{data.humidity}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span aria-hidden="true">☀</span>
              <span className="font-medium text-text-primary">{data.lux} lux</span>
            </div>
            <div>
              <span
                role="img"
                className="inline-block rounded-full px-1.5 py-0.5 text-xs font-medium"
                style={{
                  backgroundColor: `${occupancyColor}26`,
                  color: occupancyColor,
                }}
                aria-label={data.occupancy ? 'Occupied' : 'Vacant'}
              >
                {data.occupancy ? 'Occupied' : 'Vacant'}
              </span>
            </div>
          </div>

          {/* Outlet states */}
          <div className="mt-2.5 flex gap-2">
            {(
              [
                { key: 'outlet1', label: 'Outlet 1', on: data.outlet1 },
                { key: 'outlet2', label: 'Outlet 2', on: data.outlet2 },
              ] as const
            ).map(({ key, label, on }) => (
              <span
                key={key}
                role="img"
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  on ? 'bg-success/15 text-success' : 'bg-bg-elevated text-text-secondary'
                }`}
                aria-label={`${label}: ${on ? 'On' : 'Off'}`}
              >
                {label}: {on ? 'ON' : 'OFF'}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-text-secondary">Device is offline. Sensor data unavailable.</p>
      )}

      {/* Footer */}
      <p className="mt-3 text-xs text-text-secondary">
        Updated {formatRelativeTime(data.lastUpdated)}
      </p>
    </div>
  );
}
